import { AxisKey } from "./types";

// TODO(SD-837): the profession → axes mapping below is hand-curated based on
// general CHC-ability-to-occupation associations (e.g., MIT Sloan-style
// quantitative roles weight numericalReasoning + problemSolving). It is NOT
// validated against an empirical interest/ability dataset. Before relying on
// this for guidance:
//   - replace with weights sourced from a published taxonomy (e.g. O*NET)
//   - consider weighting axes per profession instead of equal-weighted average
//   - extend with locale-specific job families when this product ships outside
//     the US
export interface ProfessionDefinition {
  key: string;
  axes: AxisKey[];
}

export interface ProfessionMatch {
  key: string;
  score: number;
  axes: AxisKey[];
}

export const PROFESSIONS: ProfessionDefinition[] = [
  { key: "research", axes: ["problemSolving"] },
  { key: "engineering", axes: ["problemSolving", "numericalReasoning"] },
  { key: "medicine", axes: ["problemSolving"] },
  { key: "law", axes: ["problemSolving", "readingProcessingSpeed"] },
  { key: "software", axes: ["problemSolving", "numericalReasoning"] },
  { key: "strategy", axes: ["problemSolving"] },
  { key: "journalism", axes: ["readingProcessingSpeed"] },
  { key: "editing", axes: ["readingProcessingSpeed", "attentionSelfControl"] },
  { key: "translation", axes: ["readingProcessingSpeed"] },
  { key: "education", axes: ["readingProcessingSpeed"] },
  { key: "communications", axes: ["readingProcessingSpeed"] },
  { key: "paralegal", axes: ["readingProcessingSpeed", "attentionSelfControl"] },
  { key: "finance", axes: ["numericalReasoning", "attentionSelfControl"] },
  { key: "accounting", axes: ["numericalReasoning", "attentionSelfControl"] },
  { key: "dataAnalysis", axes: ["numericalReasoning", "problemSolving"] },
  { key: "sciences", axes: ["numericalReasoning", "problemSolving"] },
  { key: "surgery", axes: ["attentionSelfControl", "visualTrackingCoordination"] },
  { key: "qualityAssurance", axes: ["attentionSelfControl"] },
  { key: "driving", axes: ["attentionSelfControl", "visualTrackingCoordination"] },
  { key: "projectCoordination", axes: ["attentionSelfControl"] },
  { key: "design", axes: ["visualTrackingCoordination"] },
  { key: "sports", axes: ["visualTrackingCoordination"] },
  { key: "piloting", axes: ["visualTrackingCoordination", "attentionSelfControl"] },
  { key: "skilledTrades", axes: ["visualTrackingCoordination"] },
  { key: "visualArts", axes: ["visualTrackingCoordination"] },
];

export interface TopProfessionsInput {
  axisScores: Map<AxisKey, number | null>;
}

const average = (xs: number[]): number => xs.reduce((sum, v) => sum + v, 0) / xs.length;

export function topProfessions(input: TopProfessionsInput, count: number): ProfessionMatch[] {
  const matches: ProfessionMatch[] = [];

  for (const profession of PROFESSIONS) {
    const presentAxes = profession.axes.filter((a) => {
      const score = input.axisScores.get(a);
      return typeof score === "number";
    });
    if (presentAxes.length === 0) continue;

    const scores = presentAxes.map((a) => input.axisScores.get(a) as number);
    matches.push({
      key: profession.key,
      score: average(scores),
      axes: presentAxes,
    });
  }

  return matches.sort((a, b) => b.score - a.score).slice(0, count);
}
